import React, { useEffect, useRef } from 'react';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, InteractionManager, Platform } from 'react-native';
import { MatchTerm } from '@/hooks/use-order-inbound-reducer';
import { PillButton } from '@/components/pill-button';
import { SCREEN_PADDING_SMALL, SCREEN_PADDING_XS } from '@/styles';
import { useOrderInboundDetailStore } from './store/order-inbound-detail-store';

const RAPID_SCROLL_DELAY = 50;
const SCROLL_TIMEOUT_DELAY = 100;

interface PillNavProps {
  matchTermIndex: Record<string, number>;
  matchTerms: MatchTerm[];
  orderUUIDs: string[];
}

export const PillNav: React.FC<PillNavProps> = ({
  matchTermIndex,
  matchTerms,
  orderUUIDs,
}) => {
  const [styles] = useStyles();
  const listRef = useRef<FlatList>(null);
  const errorReportedRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedInputUUID = useOrderInboundDetailStore(
    (state) => state.selectedInputUUID,
  );
  const setSelectedInputUUID = useOrderInboundDetailStore(
    (state) => state.actions.setSelectedInputUUID,
  );

  // When the user manually scrolls the main RecyclerList, the selectedInputUUID is updated many times in a short
  // time frame. We have to debounce these updates to prevent trying to scroll the PillNav list before it has
  // finished measuring all the pill buttons. InteractionManager will allow us to wait for React's layout pass to finish
  // before attempting it the scroll.
  useEffect(() => {
    if (
      selectedInputUUID &&
      listRef.current &&
      matchTermIndex[selectedInputUUID] !== undefined
    ) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        InteractionManager.runAfterInteractions(() => {
          if (
            listRef.current &&
            matchTermIndex[selectedInputUUID] !== undefined
          ) {
            listRef.current.scrollToIndex({
              animated: true,
              index: matchTermIndex[selectedInputUUID],
            });
          }
        });
      }, SCROLL_TIMEOUT_DELAY);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedInputUUID, matchTermIndex]);

  return (
    <FlatList<MatchTerm>
      contentContainerStyle={[
        styles.pillNavContent,
        Platform.OS !== 'web' ? styles.pillNavContentMobile : null,
      ]}
      data={matchTerms}
      horizontal
      // This is a fallback for when the debouncing and InteractionManager fail above.
      // If scrollToIndex fails (because FlatList doesn't know the item positions yet), we wait a short delay and try
      // again without animation. This should always succeeds because the delay gives FlatList time to measure the pill
      // button dimensions. But in the case it doesn't, we should log this and adjust the scroll delay to something
      // that works better.
      onScrollToIndexFailed={async (info) => {
        await new Promise((resolve) => setTimeout(resolve, RAPID_SCROLL_DELAY));

        if (listRef.current && info.index < matchTerms.length) {
          listRef.current.scrollToIndex({
            animated: false,
            index: info.index,
          });
        }

        if (!errorReportedRef.current) {
          captureException(new Error('PillNav onScrollToIndexFailed'), {
            extra: {
              info,
              orderUUIDs,
            },
          });
          errorReportedRef.current = true;
        }
      }}
      ref={listRef}
      renderItem={({ item }) => (
        <PillButton
          button={{
            active: selectedInputUUID === item.searchInputUUID,
            label: item.term,
            onPress: () => setSelectedInputUUID(item.searchInputUUID),
          }}
        />
      )}
      showsHorizontalScrollIndicator={false}
      style={[styles.pillNav]}
    />
  );
};

const useStyles = createStyles((theme) => ({
  pillNav: {
    backgroundColor: theme.colors.white,
    flex: 1,
    maxHeight: 32 + SCREEN_PADDING_SMALL,
    paddingLeft: SCREEN_PADDING_SMALL,
    paddingVertical: SCREEN_PADDING_XS,
  },
  pillNavContent: {
    gap: 6,
    paddingRight: SCREEN_PADDING_SMALL,
  },
  pillNavContentMobile: {
    paddingRight: SCREEN_PADDING_SMALL * 2,
  },
}));
